import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, Smartphone, Save, TrendingDown } from 'lucide-react';
import ConsumerNavbar from '../../components/consumer/ConsumerNavbar';
import { consumerAPI } from '../../services/consumerAPI';
import { toast } from 'sonner';

export default function PreferencesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_alerts: true,
    push_alerts: false,
    alert_types: ['price_drop', 'expiring', 'similar']
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Run once on mount
  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const userStr = sessionStorage.getItem('consumer_user');
    if (!userStr) {
      toast.error('Please sign in to manage preferences');
      navigate('/auth');
      return;
    }

    // In production, load preferences from API
    setLoading(false);
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAlertTypeToggle = (type) => {
    setPreferences(prev => {
      const types = prev.alert_types.includes(type)
        ? prev.alert_types.filter(t => t !== type)
        : [...prev.alert_types, type];
      return { ...prev, alert_types: types };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await consumerAPI.updatePreferences(preferences);
      toast.success('Preferences saved!', {
        description: 'Your alert settings have been updated'
      });
      setSaving(false);
    } catch (error) {
      toast.error('Failed to save preferences');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ConsumerNavbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-8" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConsumerNavbar />
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Alert Preferences</h1>
          <p className="text-gray-600">
            Customize how and when you receive deal alerts
          </p>
        </div>

        {/* Alert Delivery Methods */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notification Methods
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Choose how you want to receive alerts about deals on your watchlist
          </p>

          <div className="space-y-4">
            {/* Email Alerts */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Email Alerts</div>
                  <div className="text-sm text-gray-600">Receive alerts via email</div>
                </div>
              </div>
              <button
                onClick={() => handleToggle('email_alerts')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.email_alerts ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.email_alerts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Browser Push Notifications</div>
                  <div className="text-sm text-gray-600">Get instant notifications in your browser</div>
                </div>
              </div>
              <button
                onClick={() => handleToggle('push_alerts')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  preferences.push_alerts ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences.push_alerts ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Alert Types */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5" />
            Alert Types
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Select which events should trigger an alert
          </p>

          <div className="space-y-3">
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.alert_types.includes('price_drop')}
                onChange={() => handleAlertTypeToggle('price_drop')}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div>
                <div className="font-semibold text-gray-900">Price Drops</div>
                <div className="text-sm text-gray-600">Alert me when a watched deal's price decreases</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.alert_types.includes('expiring')}
                onChange={() => handleAlertTypeToggle('expiring')}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div>
                <div className="font-semibold text-gray-900">Expiring Deals</div>
                <div className="text-sm text-gray-600">Notify me 24 hours before a deal expires</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                checked={preferences.alert_types.includes('similar')}
                onChange={() => handleAlertTypeToggle('similar')}
                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <div>
                <div className="font-semibold text-gray-900">Similar Deals</div>
                <div className="text-sm text-gray-600">Show me similar deals in the same category</div>
              </div>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong className="text-blue-700">💡 Pro Tip:</strong> Enable push notifications for instant alerts when prices drop.
            Email alerts are sent once per day with a summary of all changes.
          </p>
        </div>
      </div>
    </div>
  );
}
